import { IMsalContext, useMsal } from '@azure/msal-react';
import { useEffect, useMemo } from 'react';
import { 
  Box, 
  Stack,
  Typography,
  useTheme
} from '@mui/material';

import { ApiService } from '../../../Services/ApiService';

// Modular imports
import { 
  usePHEPlateTypeManagementState,
  PHEPlateTypeManagementProps 
} from './PHEPlateTypeManagementTypes';
import { createPHEPlateTypeManagementStyles } from './PHEPlateTypeManagementStyles';
import { usePHEPlateTypeApiOperations } from './PHEPlateTypeManagementApi';
import { 
  createPHEPlateTypeEventHandlers, 
  usePHEPlateTypeInitialization 
} from './PHEPlateTypeManagementEventHandlers';
import { 
  useFilteredData, 
  useSortedData, 
  formatSearchResultsCount 
} from './PHEPlateTypeManagementUtils';
import { 
  ActionButtons,
  LoadingComponent,
  NoResultsMessage,
  DataTable,
  FormDialog,
  DeleteConfirmationDialog,
  createTableColumns
} from './PHEPlateTypeManagementComponents';

/**
 * PHEPlateTypeManagement component for managing PHE plate types using modular architecture
 */
export const PHEPlateTypeManagement: React.FC<PHEPlateTypeManagementProps> = () => {
  const ctx: IMsalContext = useMsal();
  const theme = useTheme();
  const apiService = useMemo(() => new ApiService(ctx), [ctx]);

  // State management
  const state = usePHEPlateTypeManagementState();

  // Styles
  const styles = useMemo(() => createPHEPlateTypeManagementStyles(theme), [theme]);

  // API operations
  const apiOperations = usePHEPlateTypeApiOperations(apiService, {
    setCurrentData: state.setCurrentData,
    setDisabledForPositions: state.setDisabledForPositions,
    setIsLoading: state.setIsLoading,
    setIsDeleting: state.setIsDeleting,
    setShowDeleteConfirmation: state.setShowDeleteConfirmation,
    setMasterListOfAvailablePositions: state.setMasterListOfAvailablePositions,
    setAvailablePositions: state.setAvailablePositions,
    currentData: state.currentData,
    setUnitConfigData: state.setUnitConfigData,
    unitConfigData: state.unitConfigData
  });

  // Event handlers
  const handlers = useMemo(() => 
    createPHEPlateTypeEventHandlers(state, apiOperations), 
    [state, apiOperations]
  );

  // Data processing
  const filteredData = useFilteredData(state.currentData, state.searchTerm);
  const sortedData = useSortedData(filteredData, state.order, state.orderBy);

  // Table columns configuration
  const dataColumns = useMemo(() => 
    createTableColumns({
      disabledForPositions: state.disabledForPositions,
      isDeleting: state.isDeleting,
      editPHEtype: handlers.editPHEtype,
      setShowDeleteConfirmation: (key: string) => state.setShowDeleteConfirmation(key)
    }), 
    [state.disabledForPositions, state.isDeleting, handlers.editPHEtype, state.setShowDeleteConfirmation]
  );

  // Initialize component
  usePHEPlateTypeInitialization(apiOperations);

  // Update data columns when they change
  useEffect(() => {
    state.setDataColumns(dataColumns);
  }, [dataColumns, state.setDataColumns]);

  return (
    <Box sx={styles.container}>
      <Stack spacing={3} sx={styles.contentWrapper}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4,
          mt: 1
        }}>
        </Box>

        {/* Action Buttons */}
        <ActionButtons
          onCreateNew={handlers.handleCreateNew}
          searchTerm={state.searchTerm}
          onSearchChange={handlers.handleSearchChange}
          onClearSearch={handlers.handleClearSearch}
          isLoading={state.isLoading}
        />
        
        {/* Loading State */}
        {state.isLoading || state.currentData.length === 0 ? (
          <LoadingComponent styles={styles} />
        ) : (
          <>
            {/* No Results Message - only show when search filters out all available data */}
            {filteredData.length === 0 ? (
              <NoResultsMessage styles={styles} />
            ) : (
              <>
                {/* Data Table */}
                <DataTable
                  sortedData={sortedData}
                  dataColumns={dataColumns}
                  styles={styles}
                  order={state.order}
                  orderBy={state.orderBy}
                  onRequestSort={handlers.handleRequestSort}
                />

                {/* Results Count */}
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  {formatSearchResultsCount(filteredData.length, state.currentData.length)}
              </Typography>
              </>
            )}
          </>
        )}
        
        {/* Add/Edit PHE Type Dialog */}
        <FormDialog
          isOpen={state.isModalOpen}
          onClose={handlers.handleModalClose}
          pheTypeActive={state.pheTypeActive}
          selectedPositions={state.selectedPositions}
          availablePositions={state.availablePositions}
          text={state.text}
          itemKey={state.key}
          availableInALICE={state.availableInALICE}
          industrial={state.industrial}
          hygienic={state.hygienic}
          infoForALICE={state.infoForALICE}
          isSubmitDisabled={state.isSubmitDisabled()}
          isSaving={state.isSaving}
          styles={styles}
          onTextChange={handlers.handleTextChange}
          onKeyChange={handlers.handleKeyChange}
          onAvailableInALICEChange={handlers.handleAvailableInALICEChange}
          onIndustrialChange={state.handleIndustrialChange}
          onHygienicChange={state.handleHygienicChange}
          onInfoForALICEChange={handlers.handleInfoForALICEChange}
          onPositionSelection={state.handlePositionSelection}
          onSubmit={handlers.handleFormSubmit}
        />
        
        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          isOpen={!!state.showDeleteConfirmation}
          itemKey={state.showDeleteConfirmation || ''}
          isDeleting={state.isDeleting === state.showDeleteConfirmation}
          onClose={handlers.handleDeleteCancel}
          onConfirm={() => handlers.handleDeleteConfirm(state.currentData.find(x => x.key === state.showDeleteConfirmation)!)}
        />
      </Stack>
    </Box>
  );
};

export default PHEPlateTypeManagement; 